import React from 'react';
import { NuriCompanion } from '@/components/NuriCompanion';

const HEALTH_MODES = [
  { id: 'celiac', label: 'Celiac / Gluten-Free', icon: '🌾' },
  { id: 'mcas', label: 'MCAS', icon: '🛡' },
  { id: 'eds_mcas', label: 'EDS/MCAS', icon: '🧬' },
  { id: 'low_fodmap', label: 'Low FODMAP / IBS', icon: '🫄' },
  { id: 'glp1', label: 'GLP-1 Medication', icon: '💊' },
  { id: 'diabetes', label: 'Diabetes', icon: '🩸' },
  { id: 'heart_health', label: 'Heart Health', icon: '❤' },
  { id: 'pregnancy', label: 'Pregnancy', icon: '🤰' },
  { id: 'athlete', label: 'Performance / Athlete', icon: '🏋' },
  { id: 'senior', label: 'Senior Nutrition', icon: '🧓' },
  { id: 'pcos', label: 'PCOS', icon: '🔬' },
  { id: 'anti_inflammatory', label: 'Anti-Inflammatory', icon: '🍃' },
];

const DIETARY_TAGS = [
  'Gluten-free', 'Dairy-free', 'Vegan', 'Vegetarian',
  'Keto', 'Halal', 'Kosher', 'Paleo', 'None',
];

const COMMON_DISLIKES = [
  'Mushrooms', 'Cilantro', 'Olives', 'Blue Cheese', 'Liver',
  'Anchovies', 'Brussels Sprouts', 'Eggplant', 'Tofu', 'Oysters',
  'Beets', 'Okra', 'Cottage Cheese', 'Sardines', 'Turnips',
  'Fennel', 'Kale', 'Sauerkraut', 'Miso', 'Tempeh',
];

export function WelcomeStep() {
  return (
    <div className="flex flex-col items-center text-center pt-8" data-testid="onboarding-welcome">
      <NuriCompanion state="happy" size="lg" />
      <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 400, color: '#1A3A4A', marginTop: 24, marginBottom: 8 }}>
        Hi, I'm Nuri
      </h2>
      <p style={{ fontSize: 16, color: '#7A7A72', lineHeight: 1.6, maxWidth: 300 }}>
        I'm going to be your smartest friend for food. Let's set things up so I can help you best.
      </p>
    </div>
  );
}

export function HealthModeStep({ formData, setFormData }) {
  const selected = formData.health_modes || [];
  const toggle = (id) => {
    setFormData(prev => {
      const modes = prev.health_modes || [];
      return { ...prev, health_modes: modes.includes(id) ? modes.filter(m => m !== id) : [...modes, id] };
    });
  };
  return (
    <div data-testid="onboarding-health-mode">
      <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
        {HEALTH_MODES.map(m => (
          <button key={m.id} data-testid={`health-mode-${m.id}`}
            onClick={() => toggle(m.id)}
            className={`p-4 rounded-xl text-left transition-all border-2 ${
              selected.includes(m.id)
                ? 'border-[#1A3A4A] bg-[#1A3A4A]/5'
                : 'border-transparent bg-white/80 hover:border-[#1A3A4A]/20'
            } shadow-sm`}>
            <span className="text-xl">{m.icon}</span>
            <p className="text-sm font-medium text-[#1A3A4A] mt-2">{m.label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export function DietaryStep({ formData, setFormData }) {
  const selected = formData.dietary_restrictions || [];
  const toggle = (tag) => {
    setFormData(prev => {
      const tags = prev.dietary_restrictions || [];
      if (tag === 'None') return { ...prev, dietary_restrictions: [] };
      return { ...prev, dietary_restrictions: tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag] };
    });
  };
  return (
    <div data-testid="onboarding-dietary" className="max-w-md mx-auto">
      <div className="flex flex-wrap gap-3 justify-center">
        {DIETARY_TAGS.map(tag => (
          <button key={tag} data-testid={`dietary-${tag.toLowerCase()}`}
            onClick={() => toggle(tag)}
            className={`px-5 py-3 rounded-full text-sm font-medium transition-all border-2 ${
              (tag === 'None' && selected.length === 0) || selected.includes(tag)
                ? 'border-[#1A3A4A] bg-[#1A3A4A] text-white'
                : 'border-[#E8E3DB] bg-white text-[#1A3A4A] hover:border-[#1A3A4A]/40'
            }`}>
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
}

export function FoodDislikesStep({ formData, setFormData }) {
  const selected = formData.disliked_foods || [];
  const [custom, setCustom] = React.useState('');
  const toggle = (food) => {
    setFormData(prev => {
      const dislikes = prev.disliked_foods || [];
      return { ...prev, disliked_foods: dislikes.includes(food) ? dislikes.filter(f => f !== food) : [...dislikes, food] };
    });
  };
  const addCustom = () => {
    const trimmed = custom.trim();
    if (trimmed && !selected.includes(trimmed)) {
      setFormData(prev => ({ ...prev, disliked_foods: [...(prev.disliked_foods || []), trimmed] }));
      setCustom('');
    }
  };
  return (
    <div data-testid="onboarding-dislikes" className="max-w-md mx-auto">
      <div className="flex flex-wrap gap-2 justify-center mb-4">
        {COMMON_DISLIKES.map(food => (
          <button key={food} data-testid={`dislike-${food.toLowerCase().replace(/\s/g, '-')}`}
            onClick={() => toggle(food)}
            className={`px-4 py-2 rounded-full text-xs font-medium transition-all border ${
              selected.includes(food)
                ? 'border-[#C67A5C] bg-[#C67A5C]/10 text-[#C67A5C]'
                : 'border-[#E8E3DB] text-[#7A7A72] hover:border-[#C67A5C]/40'
            }`}>
            {food}
          </button>
        ))}
      </div>
      <div className="flex gap-2 max-w-xs mx-auto">
        <input value={custom} onChange={e => setCustom(e.target.value)} placeholder="Add custom..."
          className="flex-1 h-10 px-4 rounded-xl border border-[#E8E3DB] text-sm"
          onKeyDown={e => { if (e.key === 'Enter') addCustom(); }}
          data-testid="custom-dislike-input" />
        <button onClick={addCustom} className="h-10 px-4 rounded-xl bg-[#1A3A4A] text-white text-sm font-medium"
          data-testid="add-custom-dislike-btn">Add</button>
      </div>
    </div>
  );
}

export function QuickDemoStep() {
  return (
    <div className="text-center pt-4" data-testid="onboarding-demo">
      <NuriCompanion state="excited" size="md" />
      <div className="mt-6 max-w-sm mx-auto space-y-4">
        {[
          { icon: '📸', title: 'Photo Logging', desc: 'Snap a photo of your meal — Nuri identifies everything instantly' },
          { icon: '🔍', title: 'Nuri Score', desc: 'Scan barcodes to get a personalized quality score for any product' },
          { icon: '🍽', title: 'What to Eat', desc: 'Ask Nuri what to eat next based on your remaining daily targets' },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-4 text-left p-4 rounded-xl bg-white/80 shadow-sm">
            <span className="text-2xl">{item.icon}</span>
            <div>
              <p className="text-sm font-semibold text-[#1A3A4A]">{item.title}</p>
              <p className="text-xs text-[#7A7A72] mt-1 leading-relaxed">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
